import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { getAuth, signOut } from 'firebase/auth';

import { logoutAction } from '../../../../redux/general/actions';
import { isUserDrawerOpen } from '../../../../actions/toggleAction';
import { toggleInviteToWorkspaceModal } from '../../../../actions/modalAction';
import { getUserLicense } from '../../../../js/functions';
import UserAvatar from '../../atoms/ticket-drawer/UserAvatar';
import SettingsModal from '../modals/SettingsModal';

const SectionLabel = ({ label }) => (
  <p
    className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest"
    style={{ color: 'rgba(15,15,25,0.3)', letterSpacing: '0.09em' }}
  >
    {label}
  </p>
);

const Divider = () => (
  <div
    className="mx-4 my-1"
    style={{ height: 1, background: 'rgba(99,102,241,0.08)' }}
  />
);

const MenuItem = ({ icon, label, onClick, rightBadge, danger = false }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors rounded-lg mx-1"
    style={{
      width: 'calc(100% - 8px)',
      color: danger ? '#dc2626' : 'rgba(15,15,25,0.75)',
    }}
    onMouseEnter={(e) =>
      (e.currentTarget.style.background = danger
        ? 'rgba(239,68,68,0.06)'
        : 'rgba(99,102,241,0.06)')
    }
    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
  >
    <span
      className="w-5 flex-shrink-0 flex items-center justify-center"
      style={{ color: danger ? '#dc2626' : 'rgba(99,102,241,0.6)' }}
    >
      {icon}
    </span>
    <span className="flex-1 text-[13px] font-medium">{label}</span>
    {rightBadge}
  </button>
);

function MyAccount({ user, currentWorkspace, pendingInvites }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const auth = getAuth();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDefaultTab, setSettingsDefaultTab] = useState('profile');

  const organizationId =
    sessionStorage.getItem('organization') ||
    localStorage.getItem('organization');
  const license = getUserLicense(user, organizationId);
  const isFree = license?.toLowerCase?.()?.includes('free');
  const isUnverified = user?._id && !user?.isVerified;
  const memberCount = currentWorkspace?.members?.length ?? 0;
  const isAdmin =
    user?.organizations?.find((o) => o._id === organizationId)?.role ===
      'admin' ||
    user?.organizations?.find((o) => o._id === organizationId)?.role ===
      'superAdmin';

  const close = () => dispatch(isUserDrawerOpen(false));

  const openSettings = (tab = 'profile') => {
    setSettingsDefaultTab(tab);
    setSettingsOpen(true);
  };

  const handleSignOut = () => {
    signOut(auth).then(() => dispatch(logoutAction()));
  };

  return (
    <div className="flex flex-col h-full" style={{ background: '#f4f5fb' }}>
      {/* Profile header */}
      <div
        className="px-4 py-4"
        style={{
          background: 'white',
          borderBottom: '1px solid rgba(99,102,241,0.1)',
        }}
      >
        <div className="flex items-center gap-3">
          <UserAvatar
            fullName={user?.fullName}
            userId={user?._id}
            src={user?.avi}
            styles={{
              width: 40,
              height: 40,
              borderRadius: 10,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
          <div className="flex-1 min-w-0 flex flex-col gap-[3px]">
            <p
              className="text-[14px] leading-[14px] font-semibold truncate"
              style={{ color: 'rgba(15,15,25,0.88)' }}
            >
              {user?.fullName}
            </p>
            <p
              className="text-[10px] leading-[10px] truncate"
              style={{ color: 'rgba(15,15,25,0.4)' }}
            >
              {user?.email}
            </p>
          </div>
        </div>

        {/* Workspace row */}
        <div
          className="flex items-center justify-between mt-2 pt-2"
          style={{ borderTop: '1px solid rgba(99,102,241,0.08)' }}
        >
          <div className="min-w-0">
            <p
              className="text-[12px] leading-[12px] font-semibold truncate"
              style={{ color: 'rgba(15,15,25,0.7)' }}
            >
              {currentWorkspace?.title}
            </p>
            <p
              className="text-[10px] leading-[10px] mt-[3px]"
              style={{ color: 'rgba(15,15,25,0.35)' }}
            >
              {intl.formatMessage({
                id: 'CURRENT_WORKSPACE',
                defaultMessage: 'Current workspace',
              })}
            </p>
          </div>
          <button
            onClick={() => {
              close();
              navigate('/select-workspace');
            }}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg flex-shrink-0 ml-2 transition-colors"
            style={{
              background: 'rgba(99,102,241,0.1)',
              color: '#6366f1',
              border: '1px solid rgba(99,102,241,0.2)',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = 'rgba(99,102,241,0.18)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = 'rgba(99,102,241,0.1)')
            }
          >
            {intl.formatMessage({ id: 'SWITCH', defaultMessage: 'Switch' })}
          </button>
        </div>
      </div>

      {/* Menu items */}
      <div className="flex-1 overflow-y-auto py-2">
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'NEW_WORKSPACE',
            defaultMessage: 'New workspace',
          })}
          onClick={() => {
            close();
            navigate('/create-workspace');
          }}
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'MEMBERS',
            defaultMessage: 'Members',
          })}
          onClick={() => {
            close();
            dispatch(toggleInviteToWorkspaceModal(true));
          }}
          rightBadge={
            <span
              className="text-[12px]"
              style={{ color: 'rgba(15,15,25,0.35)' }}
            >
              {memberCount}{' '}
              {intl.formatMessage({
                id: memberCount === 1 ? 'MEMBER_SINGULAR' : 'MEMBERS_PLURAL',
                defaultMessage: memberCount === 1 ? 'member' : 'members',
              })}
            </span>
          }
        />
        <Divider />
        {isAdmin && (
          <MenuItem
            icon={
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            }
            label={intl.formatMessage({
              id: 'ADMIN_CONSOLE',
              defaultMessage: 'Admin Console',
            })}
            onClick={() => {
              close();
              navigate('/admin-console/members');
            }}
          />
        )}
        <MenuItem
          icon={
            <div className="relative">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
              </svg>
              {isUnverified && (
                <span
                  className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full flex items-center justify-center text-white"
                  style={{
                    background: '#f59e0b',
                    fontSize: 7,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  !
                </span>
              )}
            </div>
          }
          label={intl.formatMessage({
            id: 'SETTINGS',
            defaultMessage: 'Settings',
          })}
          onClick={() => openSettings(isUnverified ? 'profile' : 'profile')}
          rightBadge={
            isUnverified && (
              <span
                className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{
                  background: 'rgba(245,158,11,0.12)',
                  color: '#d97706',
                }}
              >
                {intl.formatMessage({
                  id: 'VERIFY_EMAIL_SHORT',
                  defaultMessage: 'Verify email',
                })}
              </span>
            )
          }
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'INTEGRATIONS_AND_API',
            defaultMessage: 'Integrations & API',
          })}
          onClick={() => openSettings('integrations')}
        />
        <Divider />
        <SectionLabel
          label={intl.formatMessage({
            id: 'ORGANIZATION',
            defaultMessage: 'Organization',
          })}
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="7" width="20" height="14" rx="2" />
              <path d="M16 7V5a2 2 0 00-4 0v2M12 12v3M12 15h.01" />
            </svg>
          }
          label={
            <span>
              {intl.formatMessage({
                id: 'PRICING_AND_PLANS',
                defaultMessage: 'Pricing & Plans',
              })}
            </span>
          }
          rightBadge={
            <span
              className="ml-1.5 text-[11px] font-normal"
              style={{ color: 'rgba(15,15,25,0.35)' }}
            >
              {`${license || ''}`.toUpperCase()}
            </span>
          }
          onClick={() => {
            close();
            navigate('/pricing');
          }}
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'BILLING',
            defaultMessage: 'Billing',
          })}
          onClick={() => {
            close();
            navigate('/account-management');
          }}
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
              <line x1="6" y1="14" x2="10" y2="14" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'PAYMENTS',
            defaultMessage: 'Payments',
          })}
          onClick={() => {
            close();
            navigate('/payments');
          }}
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'STORAGE_USED',
            defaultMessage: 'Storage Used',
          })}
          onClick={() => {
            close();
            navigate('/account-management');
          }}
          rightBadge={
            <span
              className="text-[12px]"
              style={{ color: 'rgba(15,15,25,0.35)' }}
            >
              {intl.formatMessage({ id: 'N/A', defaultMessage: 'N/A' })}
            </span>
          }
        />
        <Divider />
        <SectionLabel
          label={intl.formatMessage({
            id: 'SUPPORT',
            defaultMessage: 'Support',
          })}
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'TERMS_AND_CONDITIONS',
            defaultMessage: 'Terms & Conditions',
          })}
          onClick={() =>
            window.open('https://workflo.com/terms-and-conditions/', '_blank')
          }
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s-8-4.5-8-11.8A8 8 0 0112 2a8 8 0 018 8.2c0 7.3-8 11.8-8 11.8z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'PRIVACY_POLICY',
            defaultMessage: 'Privacy Policy',
          })}
          onClick={() => window.open('https://workflo.com/privacy/', '_blank')}
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'LEAVE_FEEDBACK',
            defaultMessage: 'Leave Feedback',
          })}
          onClick={() =>
            window.open(
              'https://workflo.canny.io/bugs-and-feature-requests',
              '_blank',
            )
          }
        />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'CONTACT_US',
            defaultMessage: 'Contact Us',
          })}
          onClick={() => window.open('https://workflo.com/contact/', '_blank')}
        />
        <Divider />
        <MenuItem
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          }
          label={intl.formatMessage({
            id: 'SIGN_OUT',
            defaultMessage: 'Sign out',
          })}
          onClick={handleSignOut}
          danger
        />
      </div>
      {/* Settings modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        user={user}
        defaultTab={settingsDefaultTab}
      />
    </div>
  );
}

export default MyAccount;
